import React, { useEffect, useState } from 'react';
import walletApi from '../../lib/walletApi';
import { sharedAPI } from '../../lib/walletApi';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Loader2, Building2, Save, Palette, CheckCircle2, Lock } from 'lucide-react';
import { toast } from 'sonner';

const THEME_PRESETS = [
  '#D64D7A', '#2563EB', '#059669', '#D97706', '#7C3AED',
  '#DC2626', '#0891B2', '#4F46E5', '#16A34A', '#EA580C',
];

export default function CompanySettings() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [pwSaving, setPwSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await walletApi.get('/company/profile').then(r => r.data);
        setProfile(data);
        setForm({
          name: data.name || '',
          email: data.email || '',
          phone: data.phone || '',
          address: data.address || '',
          city: data.city || '',
          state: data.state || '',
          registration_number: data.registration_number || '',
          tax_id: data.tax_id || '',
          description: data.description || '',
          theme_color: data.theme_color || '#D64D7A',
          annual_leave_days: data.annual_leave_days || 20,
          sick_leave_days: data.sick_leave_days || 10,
          work_hours: data.work_hours || '8:00 AM - 5:00 PM',
          retirement_age: data.retirement_age || 65,
        });
      } catch { toast.error('Failed to load profile'); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await walletApi.put('/company/profile', form);
      toast.success('Company profile updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const update = (key, value) => setForm(f => ({ ...f, [key]: value }));

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwForm.new_password.length < 8) { toast.error('New password must be at least 8 characters'); return; }
    if (pwForm.new_password !== pwForm.confirm) { toast.error('Passwords do not match'); return; }
    setPwSaving(true);
    try {
      await sharedAPI.changePassword(pwForm.current_password, pwForm.new_password);
      toast.success('Password changed successfully');
      setPwForm({ current_password: '', new_password: '', confirm: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally { setPwSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-pink-400" /></div>;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="company-settings-page">
      <div>
        <h1 className="text-xl font-bold text-gray-900">Company Settings</h1>
        <p className="text-sm text-gray-500">Manage your company profile and branding</p>
      </div>

      <form onSubmit={handleSave} className="space-y-5">
        {/* Basic Info */}
        <Card className="border-gray-200 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Building2 className="w-4 h-4 text-pink-500" /> Company Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Company Name</Label>
                <Input value={form.name} onChange={e => update('name', e.target.value)} className="h-9" data-testid="settings-name" />
              </div>
              <div>
                <Label className="text-xs">Email</Label>
                <Input type="email" value={form.email} onChange={e => update('email', e.target.value)} className="h-9" data-testid="settings-email" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Phone</Label>
                <Input value={form.phone} onChange={e => update('phone', e.target.value)} className="h-9" placeholder="+234 801 234 5678" />
              </div>
              <div>
                <Label className="text-xs">Registration Number (RC)</Label>
                <Input value={form.registration_number} onChange={e => update('registration_number', e.target.value)} className="h-9" placeholder="RC-123456" data-testid="settings-rc" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Tax ID</Label>
                <Input value={form.tax_id} onChange={e => update('tax_id', e.target.value)} className="h-9" placeholder="TIN-12345" />
              </div>
              <div>
                <Label className="text-xs">Address</Label>
                <Input value={form.address} onChange={e => update('address', e.target.value)} className="h-9" placeholder="42 Broad Street" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">City</Label>
                <Input value={form.city} onChange={e => update('city', e.target.value)} className="h-9" placeholder="Lagos" />
              </div>
              <div>
                <Label className="text-xs">State</Label>
                <Input value={form.state} onChange={e => update('state', e.target.value)} className="h-9" placeholder="Lagos" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Company Description */}
        <Card className="border-gray-200 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold">Company Description</CardTitle>
            <p className="text-xs text-gray-400">This appears in your offer letters</p>
          </CardHeader>
          <CardContent>
            <textarea
              value={form.description}
              onChange={e => update('description', e.target.value)}
              rows={4}
              placeholder="e.g. Excelling Great in Life Data and Steel Company is a company incorporated under the laws of the Federal Republic of Nigeria..."
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-200 focus:border-pink-400 resize-none"
              data-testid="settings-description"
            />
          </CardContent>
        </Card>

        {/* Employment Defaults */}
        <Card className="border-gray-200 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold">Employment Defaults</CardTitle>
            <p className="text-xs text-gray-400">Company-wide defaults used in offer letters. Per-employee values (type, arrangement, probation, notice) are set via CSV upload.</p>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Annual Leave (days)</Label>
                <Input type="number" value={form.annual_leave_days} onChange={e => update('annual_leave_days', parseInt(e.target.value) || 0)} className="h-9" data-testid="settings-annual-leave" />
              </div>
              <div>
                <Label className="text-xs">Sick Leave (days)</Label>
                <Input type="number" value={form.sick_leave_days} onChange={e => update('sick_leave_days', parseInt(e.target.value) || 0)} className="h-9" data-testid="settings-sick-leave" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Work Hours</Label>
                <Input value={form.work_hours} onChange={e => update('work_hours', e.target.value)} className="h-9" placeholder="8:00 AM - 5:00 PM" data-testid="settings-work-hours" />
              </div>
              <div>
                <Label className="text-xs">Retirement Age</Label>
                <Input type="number" value={form.retirement_age} onChange={e => update('retirement_age', parseInt(e.target.value) || 0)} className="h-9" data-testid="settings-retirement" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Theme Color */}
        <Card className="border-gray-200 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Palette className="w-4 h-4 text-pink-500" /> Brand Color
            </CardTitle>
            <p className="text-xs text-gray-400">Used in your offer letter PDFs</p>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex gap-2">
                {THEME_PRESETS.map(c => (
                  <button key={c} type="button" onClick={() => update('theme_color', c)}
                    className={`w-8 h-8 rounded-lg border-2 transition-all ${form.theme_color === c ? 'border-gray-900 scale-110' : 'border-transparent'}`}
                    style={{ backgroundColor: c }}
                    data-testid={`color-${c.replace('#', '')}`}
                  >
                    {form.theme_color === c && <CheckCircle2 className="w-4 h-4 text-white mx-auto" />}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <input type="color" value={form.theme_color} onChange={e => update('theme_color', e.target.value)}
                  className="w-8 h-8 rounded border-0 cursor-pointer" data-testid="color-picker" />
                <Input value={form.theme_color} onChange={e => update('theme_color', e.target.value)}
                  className="w-24 h-8 text-xs font-mono" data-testid="color-input" />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">Preview:</span>
                <div className="h-6 px-4 rounded text-white text-xs flex items-center font-medium" style={{ backgroundColor: form.theme_color }}>
                  {form.name || 'Company'}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Button type="submit" disabled={saving} className="bg-pink-500 hover:bg-pink-600 text-white" data-testid="save-settings-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Save Changes
        </Button>
      </form>

      {/* Change Password */}
      <Card className="border-gray-200 shadow-sm mt-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Lock className="w-4 h-4 text-gray-400" /> Change Password
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="space-y-3 max-w-md">
            <div>
              <Label className="text-xs">Current Password</Label>
              <Input type="password" value={pwForm.current_password} onChange={e => setPwForm(p => ({...p, current_password: e.target.value}))} required className="h-9" data-testid="current-password-input" />
            </div>
            <div>
              <Label className="text-xs">New Password</Label>
              <Input type="password" value={pwForm.new_password} onChange={e => setPwForm(p => ({...p, new_password: e.target.value}))} required minLength={8} className="h-9" data-testid="new-password-input" />
            </div>
            <div>
              <Label className="text-xs">Confirm New Password</Label>
              <Input type="password" value={pwForm.confirm} onChange={e => setPwForm(p => ({...p, confirm: e.target.value}))} required minLength={8} className="h-9" data-testid="confirm-password-input" />
            </div>
            <Button type="submit" disabled={pwSaving} variant="outline" className="border-gray-200" data-testid="change-password-btn">
              {pwSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
              Change Password
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
